// Guide's desktop app (desktop/, SAL-3174) is an Electron window that loads
// this site. Two things tell the web app it is inside that window, and they
// answer different questions:
//
// - The request. The app loads /guide?shell=desktop and sends every request
//   with a `SalesablyGuide/<version>` product token in its user agent.
//   proxy.js turns either into the DESKTOP_SHELL_HEADER request header, and
//   app/layout.js renders the page bare and transparent on it: no nav,
//   footer, banners or pinned buttons, which would float over the rep's
//   desktop. Layout is decided on the server, before any script runs, and
//   the query alone lets a browser preview that layout.
// - The bridge. The app's preload script puts `window.salesablyDesktop` on
//   the page (desktop/src/preload.js). Behaviour — shrinking to the
//   teleprompter, hiding the window, signing in through the browser — goes
//   through it, so a browser tab that merely adds the query gets the bare
//   layout and otherwise stays a tab.
//
// No imports and nothing read until a function runs: proxy.js (edge) and
// client components both import this.

export const DESKTOP_SHELL_QUERY = "shell";
export const DESKTOP_SHELL_VALUE = "desktop";
export const DESKTOP_SHELL_HEADER = "x-salesably-shell";
/** The product token desktop/src/config.js adds to the window's user agent. */
export const DESKTOP_USER_AGENT_TOKEN = "SalesablyGuide/";
/** Where the desktop app starts, and where it goes after signing in. */
export const GUIDE_DESKTOP_PATH = `/guide?${DESKTOP_SHELL_QUERY}=${DESKTOP_SHELL_VALUE}`;
/**
 * The window has no frame, so a strip of the page drags it: these classes
 * mark that strip and the controls inside it, which must opt out to stay
 * clickable. A browser ignores both.
 */
export const DRAG_REGION_CLASS = "[-webkit-app-region:drag]";
export const NO_DRAG_REGION_CLASS = "[-webkit-app-region:no-drag]";

/** Whether a request comes from the desktop window, by its query or user agent. */
export function requestsDesktopShell({ searchParams, userAgent }) {
  return (
    searchParams?.get(DESKTOP_SHELL_QUERY) === DESKTOP_SHELL_VALUE ||
    (typeof userAgent === "string" && userAgent.includes(DESKTOP_USER_AGENT_TOKEN))
  );
}

/** Whether a request reached a page through proxy.js marked as the desktop window. */
export function isDesktopShellRequest(headers) {
  return headers?.get(DESKTOP_SHELL_HEADER) === DESKTOP_SHELL_VALUE;
}

/**
 * The bridge the desktop app's preload exposes, or null in a browser.
 *
 * @typedef {object} DesktopBridge
 * @property {"darwin"|"win32"|"linux"} platform
 * @property {string} version the app's own version
 * @property {(width: number, height: number) => Promise<void>} resizeTo the
 *   window's size, as window.resizeTo would set it
 * @property {(bounds: { x?: number, y?: number, width?: number, height?: number }) => Promise<void>} setBounds
 * @property {() => Promise<void>} show
 * @property {() => Promise<void>} hide
 * @property {(url: string) => Promise<void>} openExternal in the rep's browser
 * @property {() => Promise<void>} signIn starts the browser sign-in
 * @property {(listener: (handoff: { token: string, verifier: string }) => void) => () => void} onAuthHandoff
 *   delivers each sign-in's token and verifier once; returns the unsubscribe
 * @returns {DesktopBridge|null}
 */
export function readDesktopBridge() {
  if (typeof window === "undefined") return null;
  const bridge = window.salesablyDesktop;
  return bridge && typeof bridge.resizeTo === "function" ? bridge : null;
}
